// @flow

import React, { type Node, Suspense } from 'react';
import styled from 'styled-components';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { type Country } from '@tectonics/prs/country';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { IfFeature } from '@tectonics/prs/feature';
import { CardForm } from './card-form';
import { CreditCardForm } from './credit-card-form';
import { GooglePayForm } from './google-pay-form';
import { EftForm } from './eft-form';
import { WebpayForm } from './webpay-form';
import { SafetypayOnlineForm } from './safetypay-online-form';
import { FieldArgentineCashBankList } from './argentina';
import { Empty } from '@tectonics/look-and-feel/volcanes/atoms';
import { EWalletForm } from './ewallet-form';
import { ExternalPayProviderForm } from './externalpayprovider-form';
import { MexicanForm } from './mexican-form';

const TefForm = React.lazy(() => import('./tef-form'));

export function PaymentMethodForm(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const [selectedPaymentMethod] = useUserInputValue('selectedPaymentMethod');

  if (!selectedPaymentMethod) {
    return null;
  }

  switch (selectedPaymentMethod) {
    // Use the PaymentMethodFormContent component for every new method. Let's make
    // Spec work as it's intended to be! :)

    case 'creditcard':
      return <CreditCardForm />;

    case 'debitcard':
    case 'vouchercard':
    case 'multibeneficios':
      return <CardForm />;

    case 'cash':
      return renderCashPaymentMethodForm(country);

    case 'banktransfer':
    case 'boleto':
    case 'oxxo':
    case 'oxxopay':
    case 'spei':
    case 'multicaja':
    case 'sencillito':
    case 'servipag':
    case 'pagoefectivo':
    case 'safetypay-cash':
    case 'pix':
    case 'nupay':
    case 'mpcredito':
    case 'nibbs':
      return <Empty />;

    case 'externalpayprovider':

      return renderExternalPayProviderPaymentMethodForm(country);

    case 'safetypay-online':
      return <SafetypayOnlineForm />;

    case 'webpay':
      return <WebpayForm />;

    case 'google':
      return <GooglePayForm />;

    case 'eft':
      return <EftForm />;

    case 'ewallet':
      return renderEwalletPaymentMethodForm(country);

    default:
      return <LazyLoaded selectedPaymentMethod={selectedPaymentMethod} />;
  }
}

function renderCashPaymentMethodForm(country: Country): Node {
  switch (country) {
    case 'ar':
      return <PaymentMethodFormContent />;

    case 'mx':
    case 'bo':
    case 'co':
    case 'gt':
    case 'cr':
    case 'pa':
    case 'sv':
      return <Empty />;

    default:
      throw new Error(`PaymentMethodForm - cash - there is no cash form for country: "${country}".`);
  }
}

function renderEwalletPaymentMethodForm(country: Country): Node {
  switch (country) {
    case 'br':
    case 'cl':
    case 'pe':
    case 'ke':
    case 'ng':
    case 'in':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'eg':
    case 'gh':
      return <EWalletForm country={country} />;

    default:
      return <Empty />;
  }
}

function renderExternalPayProviderPaymentMethodForm(country: Country): Node {
  switch (country) {
    case 'co':
      return <ExternalPayProviderForm />;

    case 'mx':
      return <MexicanForm />;

    default:
      return <Empty />;
  }
}

function LazyLoaded(props: { selectedPaymentMethod: string }): Node {
  return (
    <Suspense fallback={<LoadingContent />}>
      <PaymentMethodFormContentImpl {...props} />
    </Suspense>
  );
}

function LoadingContent(): Node {
  return null;
}

function PaymentMethodFormContentImpl(props: { selectedPaymentMethod: string }): Node {
  switch (props.selectedPaymentMethod) {
    case 'tef':
      return <TefForm />;
    default:
      throw new Error(`There is no form implementation for ${props.selectedPaymentMethod}`);
  }
}

function PaymentMethodFormContent(): Node {
  const allFeatures = useAllFeatures();

  return (
    <Root>

      <IfFeature allFeatures={allFeatures} wanted="field_argentine_cash_bank_list">
        <FieldArgentineCashBankList />
      </IfFeature>

    </Root>
  );
}

const Root = styled.div`
  display: grid;
  grid-gap: 2rem;
  grid-template-columns: 1fr 1fr;
  grid-auto-flow: dense;

  & > * {
    grid-column: 1/3;
  }
`;
